import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, Rocket, X, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { api } from '@/lib/api';
import { useAppStore } from '@/stores/useAppStore';

// Dashboard widget. Renders ONLY if:
//   - backend says show=true (entity <60d old AND required items remain), AND
//   - user hasn't clicked "Dismiss for now" (persists to localStorage for 24h)

const DISMISS_STORAGE_KEY = 'nexus_first_run_dismissed_until';

export function FirstRunChecklist() {
  const { activeEntity } = useAppStore();
  const [data, setData] = useState<Awaited<ReturnType<typeof api.getFirstRunChecklist>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Respect the "dismiss for 24h" soft-suppress
    try {
      const until = localStorage.getItem(DISMISS_STORAGE_KEY);
      if (until && Date.parse(until) > Date.now()) {
        setDismissed(true);
        setLoading(false);
        return;
      }
    } catch { /* */ }

    if (!activeEntity?.id) { setLoading(false); return; }
    api.getFirstRunChecklist()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [activeEntity?.id]);

  function dismiss() {
    const until = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();
    try { localStorage.setItem(DISMISS_STORAGE_KEY, until); } catch { /* */ }
    setDismissed(true);
  }

  if (loading || dismissed || !data || !data.show) return null;

  const progressPct = Math.round((data.completedRequired / data.requiredCount) * 100);

  return (
    <Card padding="lg" className="bg-gradient-to-br from-primary-50 to-white dark:from-primary-900/20 dark:to-slate-900 border-primary-200 dark:border-primary-800">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center shrink-0">
          <Rocket className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-headline text-lg font-semibold text-slate-900 dark:text-white">
            Getting started with {data.entityName || 'Nexus'}
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
            {data.completedRequired === 0
              ? `Welcome! Here's the setup checklist to get your back-office fully working.`
              : `${data.completedRequired} of ${data.requiredCount} required steps done. ${data.requiredCount - data.completedRequired} to go.`}
          </p>
        </div>
        <button
          onClick={dismiss}
          className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1"
          aria-label="Dismiss checklist for 24h"
          title="Dismiss for 24h"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex justify-between text-xs text-slate-500 mb-1">
          <span>{data.completedCount} of {data.totalCount} total</span>
          <span>{progressPct}% required complete</span>
        </div>
        <div className="bg-slate-200 dark:bg-slate-700 rounded-full h-2">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      <div className="space-y-2">
        {data.items.map(item => (
          <div
            key={item.id}
            className={`flex items-start gap-3 p-3 rounded-[var(--radius-md)] border transition-colors ${
              item.done
                ? 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-900/10'
                : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800'
            }`}
          >
            <div className="shrink-0 mt-0.5">
              {item.done ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              ) : (
                <Circle className="w-5 h-5 text-slate-300 dark:text-slate-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className={`text-sm font-medium ${
                  item.done ? 'text-slate-500 line-through' : 'text-slate-900 dark:text-white'
                }`}>
                  {item.label}
                </p>
                {item.optional && !item.done && (
                  <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 rounded">
                    optional
                  </span>
                )}
              </div>
              {!item.done && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {item.description}
                </p>
              )}
            </div>
            {!item.done && (
              <Link to={item.linkTo}>
                <Button size="sm" variant="secondary" className="gap-1 shrink-0">
                  {item.linkLabel} <ArrowRight className="w-3 h-3" />
                </Button>
              </Link>
            )}
          </div>
        ))}
      </div>

      {data.daysSinceCreation < 30 && (
        <p className="mt-4 text-xs text-slate-400 text-center">
          Entity created {data.daysSinceCreation} day{data.daysSinceCreation === 1 ? '' : 's'} ago. This checklist auto-hides after all required items complete or 60 days.
        </p>
      )}
    </Card>
  );
}
